import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Placement = 'bottom-end' | 'bottom-start' | 'top-end' | 'top-start';

interface PopoverProps {
  /** The element the popover hangs from; focus returns to it on close when focus was lost. */
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  placement?: Placement;
  /** Accessible name of the dialog. */
  ariaLabel?: string;
  testId?: string;
  className?: string;
  children: React.ReactNode;
}

const GAP = 4;
const EDGE = 8;

/**
 * A small panel anchored to a control: portalled at `z-60` (DESIGN.md §10.4), closed by Escape
 * or a mousedown outside it and its anchor, clamped to the viewport, and flipped to the other
 * side when the preferred one has no room. On close, focus returns to the anchor only when it
 * was lost with the popover.
 */
export function Popover({
  anchorRef,
  open,
  onClose,
  placement = 'bottom-end',
  ariaLabel,
  testId,
  className,
  children,
}: PopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Measure after mount (hidden until placed), then again on resize and scroll.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    function place() {
      const anchor = anchorRef.current;
      const popover = popoverRef.current;
      // A swapped-out anchor measures as all zeros; wait for the ref to point at the live one.
      if (!anchor?.isConnected || !popover) return;
      const rect = anchor.getBoundingClientRect();
      const { width, height } = popover.getBoundingClientRect();
      const wantsTop = placement.startsWith('top');
      const fitsBelow = rect.bottom + GAP + height <= window.innerHeight - EDGE;
      const fitsAbove = rect.top - GAP - height >= EDGE;
      const above = wantsTop ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove;
      const rawTop = above ? rect.top - GAP - height : rect.bottom + GAP;
      const rawLeft = placement.endsWith('end') ? rect.right - width : rect.left;
      const top = Math.max(EDGE, Math.min(rawTop, window.innerHeight - height - EDGE));
      const left = Math.max(EDGE, Math.min(rawLeft, window.innerWidth - width - EDGE));
      setPosition((current) =>
        current && current.top === top && current.left === left ? current : { top, left },
      );
    }
    place();
    // Fires after the whole commit (so after a parent re-points anchorRef) and when the content grows.
    const observer = new ResizeObserver(place);
    if (popoverRef.current) observer.observe(popoverRef.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, placement, anchorRef]);

  useEffect(() => {
    if (!open) return;
    function handleMouseDown(event: MouseEvent) {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onCloseRef.current();
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onCloseRef.current();
    }
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown, true);
      // Next frame, since the anchor may be swapped in the same commit; only when focus was
      // lost with the popover, so a caller's field focus or a click on a field keeps its target.
      requestAnimationFrame(() => {
        const active = document.activeElement;
        if (!active || active === document.body) anchorRef.current?.focus();
      });
    };
  }, [open, anchorRef]);

  if (!open) return null;
  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={ariaLabel}
      data-testid={testId}
      // The one permitted inline style: computed position, as custom properties read by the classes below.
      style={
        {
          '--popover-top': `${position?.top ?? 0}px`,
          '--popover-left': `${position?.left ?? 0}px`,
        } as React.CSSProperties
      }
      className={`fixed top-(--popover-top) left-(--popover-left) z-60 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-800 dark:bg-gray-900 ${position ? 'visible' : 'invisible'} ${className ?? ''}`}
    >
      {children}
    </div>,
    document.body,
  );
}
